import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { t } from "@/i18n/id";
import { Label } from "./label";

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  /** Character counter: current length and max. */
  count?: { value: number; max: number };
  optional?: boolean;
  className?: string;
  labelAction?: ReactNode;
  id?: string;
  children: ReactElement<Record<string, unknown>>;
}

/** Label + control + hint/error + optional counter, wired with aria attributes. */
export function Field({ label, hint, error, count, optional, className, labelAction, id: idProp, children }: FieldProps) {
  const auto = useId();
  const id = idProp ?? (children.props.id as string | undefined) ?? auto;
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(" ") || undefined;
  const over = count ? count.value > count.max : false;
  const control = isValidElement(children)
    ? cloneElement(children, {
        id,
        "aria-invalid": error || over ? true : undefined,
        "aria-describedby": describedBy,
      })
    : children;
  return (
    <div className={cn("space-y-1.5", className)}>
      {label || count || labelAction ? (
        <div className="flex items-end justify-between gap-3">
          {label ? (
            <Label htmlFor={id}>
              {label}
              {optional ? <span className="ml-1 font-normal text-slate-400">({t.common.optional})</span> : null}
            </Label>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            {labelAction}
            {count ? (
              <span className={cn("text-xs tabular-nums", over ? "font-semibold text-danger" : count.value > count.max * 0.9 ? "text-warning" : "text-slate-400")} aria-live="polite">
                {t.common.chars(count.value, count.max)}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
      {control}
      {error ? (
        <p id={errId} className="text-[13px] font-medium text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-[13px] text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
